import { lighten } from '@mui/system'

import { STATES } from 'components/webhooks/webhook-utils'
import theme from 'theme'
import { ApiKeyStatus } from 'types/api/api-key'
import { ClientState, ClientStatus } from 'types/api/attacks/client'

export function getColorForClientState(state: ClientState): string {
  switch (state.toLowerCase()) {
    case 'active':
      return theme.colors.green[0]
    case 'revoked':
      return theme.colors.red[0]
    case 'idle':
      return theme.colors.gray[2]
    case 'starting':
      return theme.colors.orange[0]
    default:
      return theme.colors.steel[1]
  }
}

export function getColorForClientStatus(clientStatus: ClientStatus): string {
  switch (clientStatus) {
    case 'HEALTHY':
      return theme.colors.green[0]
    case 'UNHEALTHY':
      return theme.colors.red[0]
    case 'IMPAIRED':
      return theme.colors.orange[0]
    default:
      return theme.colors.steel[1]
  }
}

export function getColorForWebhookState(state: string): string {
  switch (state) {
    case STATES.ACTIVE:
      return theme.colors.green[0]
    case STATES.DEACTIVATED:
      return theme.colors.red[0]
    default:
      return theme.colors.steel[1]
  }
}

export enum ScenarioState {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  SETUPFAILED = 'SETUPFAILED',
  INPUTRESULTS = 'INPUTRESULTS',
  ACTIVE = 'ACTIVE',
  SCHEDULED = 'SCHEDULED',
  HALTED = 'HALTED'
}

/**
 *
 * @param {string} state state of scenario and attack runs on scenario details
 */
export function getColorForScenarioState(state: ScenarioState): string {
  switch (state.toLowerCase()) {
    case 'completed':
      return theme.colors.green[0]
    case 'failed':
    case 'setupfailed':
      return theme.colors.red[0]
    case 'inputresults':
      return theme.colors.orange[0]
    case 'active':
      return theme.colors.blue[0]
    case 'scheduled':
      return theme.colors.steel[0]
    case 'halted':
      return theme.colors.steel[1]
    default:
      return theme.colors.steel[1]
  }
}

export function getColorForScenarioResult(scenarioResult: string): string {
  switch (scenarioResult.toLowerCase()) {
    case 'passed':
      return theme.colors.green[0]
    case 'failed':
      return theme.colors.red[0]
    case 'unsure':
      return theme.colors.steel[1]
    case 'input results':
      return theme.colors.orange[0]
    default:
      return theme.colors.steel[1]
  }
}

export function getColorForApiKeyState(state: ApiKeyStatus): string {
  switch (state) {
    case 'ACTIVE':
      return theme.colors.green[0]
    case 'REVOKED':
      return theme.colors.red[0]
    default:
      return theme.colors.steel[1]
  }
}

/**
 * Contains logic to get the color of a linear gradient at one particular position
 * Takes in the beginning and ending color in the Hex array and the position ratio of the color
 * compared to the width of the container with the gradient
 * @param  {array} color1
 * @param  {array} color2
 * @param  {number} percent,
 *
 * @return {string}
 */
export function getHexFromLinearGradient(
  color1: number[],
  color2: number[],
  percent: number
): string {
  const colorRatio = percent * 2 - 1
  const color1Ratio = (colorRatio / 1 + 1) / 2
  const color2Ratio = 1 - color1Ratio

  const rgb = `rgb(${Math.round(
    color1[0] * color1Ratio + color2[0] * color2Ratio
  )},${Math.round(
    color1[1] * color1Ratio + color2[1] * color2Ratio
  )},${Math.round(color1[2] * color1Ratio + color2[2] * color2Ratio)})`
  return rgb
}

// take a color from the MUI theme and lighten it a bit for use as a background color
export function getLightMuiBackgroundColor(color) {
  return lighten(color, 0.9)
}
